import { Controller } from '@hotwired/stimulus';
import { get, post } from "@rails/request.js"

let debounce = require("lodash.debounce");

export default class extends Controller {
  static targets = [
    "hidden",
    "results",
    "wrapper",
    "selection",
    "input",
    "searcher",
    "field",
    "feedback",
    "length",
    "notfound",
    "geolocate",
    "geolocateLabel",
    "located",
    "addPrompt",
    "addForm",
    "addName",
    "addLocality",
    "addCountry",
    "addError",
    "addSubmit"
  ];
  static values = {
    schoolId: Number,
    schoolName: String,
    open: Boolean,
    hasSubmissions: Boolean
  }

  initialize(){
    this.search = debounce(this.search, 300).bind(this);
  }

  connect() {
    // `open` keeps the search field showing (with the current school_id retained
    // in the hidden field) instead of collapsing to the selected-school chip.
    if ( this.schoolIdValue && this.schoolNameValue && !this.openValue ){
      this.setSchool(this.schoolIdValue, this.schoolNameValue);
    }

    this.previousLink = document.getElementById('previous_step_link');
    this.isOpen = false;
    // The gym currently committed to the hidden field, tracked separately from
    // the in-progress search so closing the picker (✕) can revert to it and the
    // chip's remove control can clear it. Seeded from the rendered school.
    this.committedSchoolId = ( this.schoolIdValue && this.schoolNameValue ) ? this.schoolIdValue : null;
    this.committedSchoolName = this.committedSchoolId ? this.schoolNameValue : null;
    this.blurTimeout = null;
    // Coordinates from "Find schools near me". Once set, every search is weighted
    // toward them (and the 3-character minimum is waived).
    this.coords = null;
    this.boundViewportResize = this.viewportResize.bind(this);
    window.visualViewport?.addEventListener('resize', this.boundViewportResize);

    // "Find schools near me" needs the Geolocation API; hide the affordance
    // entirely where it isn't available rather than offer a dead button.
    if ( this.hasGeolocateLabelTarget ) {
      this.geolocateDefaultLabel = this.geolocateLabelTarget.textContent;
    }
    if ( this.hasGeolocateTarget && !('geolocation' in navigator) ) {
      this.geolocateTarget.classList.add('hidden');
    }

    // The account "Home gym" editor opens straight into search mode (no chip),
    // so pin the student's own gyms here too — openSearch covers the onboarding
    // chip-tap path.
    if ( this.openValue ) this.loadSubmittedSchools();
  }

  disconnect() {
    window.visualViewport?.removeEventListener('resize', this.boundViewportResize);
  }

  viewportResize() {
    // The body-height/scroll juggling below is an onboarding-only workaround for
    // its absolute-positioned sheet. The account page (body.students) uses a
    // `fixed` full-screen sheet that already clears the keyboard via CSS; running
    // this there fights the fixed positioning and makes the picker jump around.
    if ( !document.body.classList.contains('onboarding') ) return;

    if ( this.isOpen ) {
      const visibleHeight = window.visualViewport.height;

      document.body.style.height = `${visibleHeight}px`;
      this.element.style.height = `${visibleHeight}px`;

      const resultsBottom = this.inputTarget.getBoundingClientRect().bottom;
      const resultsHeight = visibleHeight - resultsBottom;
      this.resultsTarget.style.height = `${resultsHeight}px`;
    } else {
      document.body.style.height = 'auto';
      this.element.style.height = 'auto';
      this.resultsTarget.style.height = 'auto';
    }

    // Reset the viewport scroll
    window.scrollTo(0, 0);

    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 150);
  }

  search(e) {
    // With coordinates we can search immediately — proximity needs no query, so
    // the 3-character minimum only applies to a cold name/location lookup.
    if ( this.coords || this.inputTarget.value.length >= 3 ) {
      this.performSearch();
    } else {
      this.lengthTarget.classList.remove('hidden');
      this.resultsTarget.classList.add('hidden');
      this.resultsTarget.innerHTML = "";
      this.notfoundTarget.classList.add('hidden');
    }
  }

  // Single request path for both name search and geolocation. The typed query
  // (if any) narrows the results; the coordinates (if any) weight them by
  // proximity server-side, replacing the timezone nudge.
  performSearch() {
    this.lengthTarget.classList.add('hidden');

    const query = {
      query: this.inputTarget.value,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    if ( this.coords ) {
      query.latitude = this.coords.latitude;
      query.longitude = this.coords.longitude;
    }

    get(this.inputTarget.dataset.url, {
      responseKind: "turbo-stream",
      query
    }).then((response) => {
      // A missing header (e.g. a degraded geo response) parses to NaN; treat that
      // as "no results" too so we never land on a blank sheet with no message.
      const resultCount = parseInt(response.headers.get("Nuveer-Total-Results"));
      const empty = !resultCount;
      this.notfoundTarget.classList.toggle('hidden', !empty);
      this.resultsTarget.classList.toggle('hidden', empty);
      // Now that a search has run, offer the "add a missing gym" path.
      if ( this.hasAddPromptTarget ) this.addPromptTarget.classList.remove('hidden');
    });
  }

  useLocation(e) {
    e.preventDefault();
    if ( !('geolocation' in navigator) ) return;

    // Tapping this button blurs a focused input, which schedules the results to
    // hide (see #blur). Cancel that so the nearby schools we're about to fetch
    // aren't hidden out from under us a moment after they appear.
    this.cancelBlurHide();

    this.geolocateTarget.disabled = true;
    this.geolocateLabelTarget.textContent = "Finding your location…";

    navigator.geolocation.getCurrentPosition(
      (position) => this.locationFound(position),
      () => this.locationError(),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  locationFound(position) {
    // getCurrentPosition is async; cancel again in case a blur slipped through
    // between the tap and the position resolving.
    this.cancelBlurHide();
    this.coords = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude
    };
    this.resetGeolocate();
    // Swap the "Find schools near me" button for a "Showing schools near you"
    // status chip so the active state is visible and (crucially) removable —
    // a second click on a still-live link was the source of the blank-results
    // confusion. The coordinates persist, so a name search stays proximity
    // weighted; "Clear" drops them again (see clearLocation).
    this.showLocated();
    // Open the picker (the same full-screen takeover focusing the input gives)
    // and search straight away — coordinates alone surface the nearest schools.
    // Don't steal focus: that would raise the keyboard over the very results we
    // just fetched; the student can tap the input when they want to type a name.
    this.isOpen = true;
    this.previousLink?.classList.add('hidden');
    this.element.classList.add('school-search--active');
    this.performSearch();
  }

  locationError() {
    this.geolocateTarget.disabled = false;
    this.geolocateLabelTarget.textContent = "Couldn't get your location — try searching instead";
  }

  resetGeolocate() {
    this.geolocateTarget.disabled = false;
    this.geolocateLabelTarget.textContent = this.geolocateDefaultLabel;
  }

  showLocated() {
    this.geolocateTarget.classList.add('hidden');
    if ( this.hasLocatedTarget ) this.locatedTarget.classList.remove('hidden');
  }

  // Drop any acquired coordinates and swap the active proximity chip back for
  // the "Find schools near me" button.
  resetLocation() {
    this.coords = null;
    if ( this.hasLocatedTarget ) this.locatedTarget.classList.add('hidden');
    this.geolocateTarget.classList.remove('hidden');
    this.resetGeolocate();
  }

  // Drop the proximity weighting and return to a plain name/location search.
  clearLocation(e) {
    e?.preventDefault();
    this.resetLocation();

    if ( this.inputTarget.value.length >= 3 ) {
      this.performSearch();
    } else {
      // Nothing left to search on — clear the proximity results and bring back
      // the three-character hint rather than leaving stale nearby schools.
      this.resultsTarget.innerHTML = "";
      this.resultsTarget.classList.add('hidden');
      this.notfoundTarget.classList.add('hidden');
      this.lengthTarget.classList.remove('hidden');
    }
  }

  select(e) {
    const schoolId = parseInt(e.currentTarget.dataset.schoolId);
    const schoolName = e.currentTarget.dataset.schoolName;
    this.setSchool(schoolId, schoolName);
  }

  setSchool(schoolId, schoolName){
    this.committedSchoolId = schoolId;
    this.committedSchoolName = schoolName;
    this.isOpen = false;
    this.previousLink?.classList.remove('hidden');
    this.hiddenTarget.value = schoolId;
    this.selectionTarget.innerHTML = schoolName;
    this.wrapperTarget.classList.remove('hidden');
    this.searcherTarget.classList.add('hidden');
    this.resultsTarget.classList.add('hidden');
    this.hideAddForm();
    if ( this.hasAddPromptTarget ) this.addPromptTarget.classList.add('hidden');
    this.element.classList.remove('school-search--active');
  }

  closePicker(){
    // Revert to the last committed state rather than leaving the in-progress
    // search (typed query + rendered results) bleeding onto the step page.
    if ( this.committedSchoolId && !this.openValue ) {
      // A gym was chosen earlier: collapse back to its chip, discarding the
      // half-finished search.
      this.setSchool(this.committedSchoolId, this.committedSchoolName);
      return;
    }

    // Nothing committed (or the always-open account editor, which never shows a
    // chip): return to a pristine empty search and restore the committed id, if
    // any, to the hidden field.
    this.isOpen = false;
    this.previousLink?.classList.remove('hidden');
    this.element.classList.remove('school-search--active');
    this.resetSearchState();
    this.hiddenTarget.value = this.committedSchoolId || "";
  }

  // Clear the chosen gym in place — back to an empty search — for a student who
  // picked the wrong gym (or none) without leaving the step via the skip button.
  removeSchool(e){
    e?.preventDefault();
    e?.stopPropagation(); // the chip wrapper itself opens the search on click
    this.committedSchoolId = null;
    this.committedSchoolName = null;
    this.hiddenTarget.value = "";
    this.selectionTarget.innerHTML = "";
    this.wrapperTarget.classList.add('hidden');
    this.searcherTarget.classList.remove('hidden');
    this.resetSearchState();
  }

  openSearch(e){
    this.isOpen = true;
    this.previousLink?.classList.add('hidden');
    this.selectionTarget.innerHTML = "";
    this.wrapperTarget.classList.add('hidden');
    this.searcherTarget.classList.remove('hidden');

    // Keep the committed gym in the hidden field while searching — opening to
    // browse shouldn't clear the saved gym. Only picking a new one (setSchool),
    // an explicit remove (removeSchool) or closing without a pick (closePicker)
    // changes it. So submitting without re-selecting preserves the current gym.

    // Start fresh rather than reopening onto a stale query/results list. The
    // committed gym is preserved (closePicker can still revert to it).
    this.resetSearchState();

    this.loadSubmittedSchools();
    this.inputTarget.focus();
  }

  // Wipe the in-progress search back to a pristine empty state (no query, no
  // results, no proximity weighting), restoring the default search affordances
  // (the inner field/feedback/geolocate may have been hidden by the add-a-gym
  // form). Leaves the hidden school_id untouched — callers set that.
  resetSearchState(){
    this.showSearchView();
    this.inputTarget.value = "";
    this.resultsTarget.innerHTML = "";
    this.resultsTarget.classList.add('hidden');
    this.notfoundTarget.classList.add('hidden');
    if ( this.hasAddPromptTarget ) this.addPromptTarget.classList.add('hidden');
    this.lengthTarget.classList.remove('hidden');
    this.resetLocation();
  }

  // Bring back the default search UI, undoing whatever the add-a-gym form hid.
  showSearchView(){
    this.hideAddForm();
    this.fieldTarget.classList.remove('hidden');
    this.feedbackTarget.classList.remove('hidden');
    this.restoreGeolocateRow();
  }

  // Pre-populate the empty picker with the student's own submitted gyms. They
  // aren't in the global corpus, so this saves retyping a name only they know.
  // A no-op when there's nothing to pin or the student starts from a query.
  loadSubmittedSchools(){
    if ( !this.hasSubmissionsValue || this.inputTarget.value.length > 0 || this.coords ) return;

    get(this.inputTarget.dataset.url, {
      responseKind: "turbo-stream",
      query: { submitted: 1 }
    }).then((response) => {
      // Don't clobber results if the student has since typed a query.
      if ( this.inputTarget.value.length > 0 ) return;
      const count = parseInt(response.headers.get("Nuveer-Total-Results"));
      this.resultsTarget.classList.toggle('hidden', !count);
    });
  }

  // --- Add a missing gym ---------------------------------------------------

  showAddForm(e){
    e?.preventDefault();
    // Swap the search affordances for the add form, prefilling the gym name
    // with whatever the student last searched for.
    this.fieldTarget.classList.add('hidden');
    this.feedbackTarget.classList.add('hidden');
    this.geolocateTarget.classList.add('hidden');
    if ( this.hasLocatedTarget ) this.locatedTarget.classList.add('hidden');
    this.resultsTarget.classList.add('hidden');
    if ( this.hasAddPromptTarget ) this.addPromptTarget.classList.add('hidden');

    this.addErrorTarget.classList.add('hidden');
    if ( !this.addNameTarget.value ) this.addNameTarget.value = this.inputTarget.value.trim();
    this.addFormTarget.classList.remove('hidden');
    this.addNameTarget.focus();
  }

  cancelAddForm(e){
    e?.preventDefault();
    this.showSearchView();
    // Restore the results/feedback for the current query (and re-show the prompt).
    this.search(e);
  }

  hideAddForm(){
    if ( this.hasAddFormTarget ) this.addFormTarget.classList.add('hidden');
  }

  // Show whichever proximity affordance matches the current state: the active
  // "near you" chip if we have coordinates, otherwise the geolocate button.
  restoreGeolocateRow(){
    if ( this.coords ) {
      this.geolocateTarget.classList.add('hidden');
      if ( this.hasLocatedTarget ) this.locatedTarget.classList.remove('hidden');
    } else {
      this.geolocateTarget.classList.remove('hidden');
      if ( this.hasLocatedTarget ) this.locatedTarget.classList.add('hidden');
    }
  }

  createSchool(e){
    e?.preventDefault();
    const name = this.addNameTarget.value.trim();
    if ( !name ) {
      this.showAddError("Please enter your gym's name.");
      this.addNameTarget.focus();
      return;
    }

    this.addSubmitTarget.disabled = true;
    this.addErrorTarget.classList.add('hidden');

    post(this.inputTarget.dataset.url, {
      responseKind: "json",
      body: {
        school: {
          name,
          submitted_locality: this.addLocalityTarget.value.trim(),
          submitted_country: this.addCountryTarget.value.trim()
        }
      }
    }).then(async (response) => {
      this.addSubmitTarget.disabled = false;
      if ( response.ok ) {
        const school = await response.json;
        // So reopening the picker pins this gym straight away (the value was
        // rendered before the student had any submissions).
        this.hasSubmissionsValue = true;
        this.setSchool(school.id, school.name);
      } else {
        this.showAddError("Sorry, we couldn't add that gym. Please try again.");
      }
    }).catch(() => {
      this.addSubmitTarget.disabled = false;
      this.showAddError("Sorry, we couldn't add that gym. Please try again.");
    });
  }

  showAddError(message){
    this.addErrorTarget.textContent = message;
    this.addErrorTarget.classList.remove('hidden');
  }

  blur(e){
    // Only the inline desktop dropdown collapses when focus leaves the input.
    // The mobile full-screen sheet must NOT: there, tapping blank space is how
    // you dismiss the keyboard to read the list — hiding it would strand the
    // student with results they can no longer tap. They close the sheet with
    // the ✕ / Clear / by selecting a school instead.
    if ( window.matchMedia('(max-width: 767px)').matches ) return;

    // Delay so a tap on a result registers (select fires) before we hide the
    // list. Stored so deliberate takeovers (geolocation) can cancel it.
    this.blurTimeout = setTimeout(() => {
      this.resultsTarget.classList.add('hidden');
    }, 200);
  }

  cancelBlurHide(){
    if ( this.blurTimeout ) {
      clearTimeout(this.blurTimeout);
      this.blurTimeout = null;
    }
  }

  focus(e){
    // Onboarding opens the picker by focusing the input rather than tapping the
    // selected-school chip (openSearch), so mirror its housekeeping here: mark
    // the picker open and hide the "Back to ..." link that would otherwise float
    // over the full-screen sheet.
    this.isOpen = true;
    this.previousLink?.classList.add('hidden');
    this.element.classList.add('school-search--active');
    if ( this.coords || this.inputTarget.value.length > 2 ){
      this.search(e);
    } else {
      // Empty + not located: pin the student's own gyms. Onboarding renders the
      // picker with open:false, so connect() didn't (it only does for the
      // always-open account editor) — without this their submissions only
      // appeared after a select-and-reopen via the chip.
      this.loadSubmittedSchools();
    }
  }
}
